import { v4 as getUniqueIdImpl } from 'uuid';

import { getDirectRelationship } from '@cognite/apm-client';
import type {
  CogniteAsset,
  CogniteFile,
  InstanceId,
  InstanceOrExternalId,
} from '@cognite/apm-client';
import type {
  CogniteClient,
  FileUploadResponse,
  HttpResponse,
  ItemsResponse,
} from '@cognite/sdk';

import type { InfieldViewReferences } from '../../../constants/cdm-infield-data-model';
import { VIEW_REFERENCES_CDM_SOLUTION_MODEL } from '../../../constants/cdm-infield-data-model';

import type { IIdmMediaService } from './idm-media-service.interface';
import {
  uploadToGCS as uploadToGCSImpl,
  uploadViaAxios as uploadViaAxiosImpl,
} from './utils';

export interface CogniteClientDependencies {
  project: CogniteClient['project'];
  instances: {
    upsert: CogniteClient['instances']['upsert'];
    retrieve: CogniteClient['instances']['retrieve'];
  };
  post: CogniteClient['post'];
}

export type UploadRequestResponse = HttpResponse<
  ItemsResponse<FileUploadResponse>
>;

export interface IIdmMediaServiceDependencies {
  uploadViaAxios: typeof uploadViaAxiosImpl;
  uploadToGCS: typeof uploadToGCSImpl;
  getUniqueId: () => string;
}

export type InfieldViewReferencesDependency = Pick<
  InfieldViewReferences,
  'cdmFile'
>;

const defaultDependencies: IIdmMediaServiceDependencies = {
  uploadViaAxios: uploadViaAxiosImpl,
  uploadToGCS: uploadToGCSImpl,
  getUniqueId: getUniqueIdImpl,
};

export class IdmMediaService implements IIdmMediaService {
  private cogniteClient: CogniteClientDependencies;
  private dependencies: IIdmMediaServiceDependencies;
  private viewReferences?: InfieldViewReferencesDependency;
  constructor(
    client: CogniteClientDependencies,
    dependencies = defaultDependencies,
    viewReferences?: InfieldViewReferencesDependency
  ) {
    this.cogniteClient = client;
    this.dependencies = dependencies;
    this.viewReferences = viewReferences;
  }
  /**
   * Uploads a file node `CogniteFile` into FDM model and then streams
   * the file’s binary content to the project’s file storage (GCS or S3 depending on setup).
   * @param file              The file object selected by the user (binary + metadata).
   * @param externalId        Optional externalId identifier. Defaults to a generated UUID.
   * @param space             The FDM space where the `CogniteFile` node is created.
   * @param isAwsProject      Flag to determine if upload should go to AWS (S3) or GCS.
   * @param assetInstanceIds  Optional list of assets to relate the file to.
   *
   * @returns { externalId: string; space: string } - The unique reference to the created file node.
   */
  public async uploadMedia({
    file,
    description,
    externalId,
    space,
    isAwsProject,
    assetInstanceIds = [],
  }: {
    file: File;
    description?: string;
    externalId?: string;
    space: string;
    isAwsProject: boolean;
    assetInstanceIds?: InstanceOrExternalId[];
  }) {
    const contextualizedIdmAssets = () => {
      if (assetInstanceIds && assetInstanceIds.length > 0)
        return assetInstanceIds.map(
          ({ externalId, space }) =>
            ({
              externalId,
              space: space || '',
            }) as InstanceId
        );
      return undefined;
    };

    const assetRelationships = contextualizedIdmAssets()
      ?.map((assetInstanceId) => getDirectRelationship(assetInstanceId))
      .filter(Boolean);

    const fileExternalId = externalId || this.dependencies.getUniqueId();

    const cogniteFile: Partial<CogniteFile> = {
      name: file.name ?? fileExternalId,
      description,
      mimeType: file.type,
      assets: assetRelationships as CogniteAsset[],
    };

    await this.cogniteClient.instances.upsert({
      items: [
        {
          instanceType: 'node',
          space,
          externalId: fileExternalId,
          sources: [
            {
              source:
                this.viewReferences?.cdmFile ??
                VIEW_REFERENCES_CDM_SOLUTION_MODEL.cdmFile,
              properties: {
                ...cogniteFile,
              },
            },
          ],
        },
      ],
    });

    const uploadUrl = await this.cogniteClient
      .post<{ items: FileUploadResponse[] }>(
        `/api/v1/projects/${this.cogniteClient.project}/files/uploadlink`,
        {
          data: {
            items: [
              {
                instanceId: {
                  externalId: fileExternalId,
                  space,
                },
              },
            ],
          },
        }
      )
      .then((res) => {
        const uploadUrl = res.data.items[0]?.uploadUrl;

        if (!uploadUrl) {
          throw new Error(
            'Failed to get upload URL from Cognite API response.'
          );
        }

        return uploadUrl;
      });

    if (isAwsProject) {
      /**
       * AWS projects require a different upload method than UploadGCS allows.
       * We will make a more permanent solution soon.
       */
      await this.dependencies.uploadViaAxios({ file, url: uploadUrl });
    } else {
      await this.dependencies.uploadToGCS({ file, url: uploadUrl });
    }

    return { externalId: fileExternalId, space };
  }

  /**
   * Updates metadata for existing file nodes in FDM.
   * Uses upsert to update only the properties without touching the file binary.
   * @param items Array of files to update with their properties.
   *
   * @returns Array of { externalId: string; space: string } - References to the updated file nodes.
   */
  public async updateMedia({
    items,
  }: {
    items: Array<{
      externalId: string;
      space: string;
      properties: Partial<CogniteFile>;
    }>;
  }) {
    await this.cogniteClient.instances.upsert({
      items: items.map((item) => ({
        instanceType: 'node' as const,
        externalId: item.externalId,
        space: item.space,
        sources: [
          {
            source:
              this.viewReferences?.cdmFile ??
              VIEW_REFERENCES_CDM_SOLUTION_MODEL.cdmFile,
            properties: item.properties,
          },
        ],
      })),
    });

    return items.map(({ externalId, space }) => ({ externalId, space }));
  }

  /**
   * Returns the subset of the given file instance ids that still exist as
   * nodes. CDM `instances.upsert` is create-or-update, so writing to a file
   * that was deleted (e.g. from asset exploration) would resurrect it as an
   * empty node. Callers use this to only update files that still exist.
   */
  public async getExistingFileInstanceIds(
    ids: InstanceId[]
  ): Promise<InstanceId[]> {
    if (ids.length === 0) {
      return [];
    }

    const { items } = await this.cogniteClient.instances.retrieve({
      items: ids.map(({ externalId, space }) => ({
        instanceType: 'node' as const,
        externalId,
        space,
      })),
    });

    return items
      .filter((item) => item.instanceType === 'node')
      .map(({ externalId, space }) => ({ externalId, space }));
  }
}

export const createIdmMediaService = (
  ...args: ConstructorParameters<typeof IdmMediaService>
): IIdmMediaService => new IdmMediaService(...args);
